import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { findIndex } from 'lodash';
import { ProfileContext } from '../../contexts/ProfileContext';
import { useTranslation } from 'react-i18next';
import { PendingVideoIcon, ProcessingVideoIcon, CompleteVideoIcon, EditVideoIcon } from '../icons';
import { checkVideoUrlValidity, getTranslation } from '../../utils';

export default function VideoSection({ orgPrefix }) {
  const { t, i18n } = useTranslation();
  const [profile] = useContext(ProfileContext);

  const { profileTypeData: { exampleVideo, videos = [] } = {} } = profile;

  return (
    <div className="mt-8">
      <h2
        className="text-lg text-black font-bold my-4"
        dangerouslySetInnerHTML={{
          __html: getTranslation(t, 'myProfile.videosHeading', orgPrefix)
        }}
      />
      <div className="relative min-h-[300px]">
        <div className="flex flex-row overflow-x-auto gap-4 pr-12">
          {exampleVideo?.thumbnail?.[i18n.language] && (
            <ExampleVideoThumbnail exampleVideo={exampleVideo} />
          )}
          {videos?.length > 0 &&
            videos?.map(({ videoPage: { index, profilePageTitle, profilePageDescription } }) => {
              return (
                <VideoThumbnail
                  key={index}
                  videoId={index}
                  title={profilePageTitle?.[i18n.language]}
                  description={profilePageDescription?.[i18n.language]}
                  videos={profile?.videos?.videos}
                  orgPrefix={orgPrefix}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
}

const ExampleVideoThumbnail = ({ exampleVideo }) => {
  const { i18n } = useTranslation();

  const { title, description, thumbnail, video } = exampleVideo;

  const backgroundFullLink = process.env.REACT_APP_AWS_URL + thumbnail[i18n.language];

  if (!video[i18n.language]) return null;

  return (
    <Link
      to={`/example-video`}
      className={`block relative border-[1px] w-[80%] max-w-[80%] min-w-[80%] md:w-[40%] md:min-w-[40%] md:max-w-[40%] border-black rounded-xl overflow-hidden p-6`}>
      <img
        src={backgroundFullLink}
        alt="Example video background"
        className="w-full h-full block object-cover object-center absolute left-0 top-0 opacity-50 -z-10"
      />

      {title[i18n.language] && (
        <p
          className="text-lg font-bold text-black mb-2"
          dangerouslySetInnerHTML={{
            __html: title[i18n.language]
          }}
        />
      )}
      {description[i18n.language] && (
        <p
          className="text-base font-normal text-grey-200 mb-10"
          dangerouslySetInnerHTML={{
            __html: description[i18n.language]
          }}
        />
      )}

      <span className="block mb-6 opacity-0 pointer-events-none">
        <ProcessingVideoIcon />
      </span>

      <div className="flex w-full justify-end">
        <EditVideoIcon />
      </div>
    </Link>
  );
};

const VideoThumbnail = ({ videoId, title, description, videos }) => {
  const VIDEO_URL = process.env.REACT_APP_AWS_URL + 'profile-videos/';

  const [status, setStatus] = useState('pending'); // pending, processing, complete

  const setVideoStatus = async () => {
    let index = findIndex(videos, ({ videoID: tVideoID }) => parseInt(tVideoID) === videoId);
    if (index >= 0) {
      const vidURL = videos[index].videoURL;
      if (vidURL === undefined) {
        setStatus('pending');
        return;
      }
      const videoURL = VIDEO_URL + vidURL;
      const isValid = await checkVideoUrlValidity(videoURL);
      if (!isValid) {
        setStatus('processing');
      } else {
        setStatus('complete');
      }
    }
  };

  useEffect(() => {
    setVideoStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return (
    <Link
      to={`/video/${videoId}`}
      className={`block border-[1px] w-[80%] max-w-[80%] min-w-[80%] md:w-[30%] md:min-w-[30%] md:max-w-[30%] border-black rounded-xl p-6`}>
      <span className="block mb-6">
        {status === 'pending' && <PendingVideoIcon />}
        {status === 'processing' && <ProcessingVideoIcon />}
        {status === 'complete' && <CompleteVideoIcon />}
      </span>
      <p
        className="text-lg font-bold text-black mb-2"
        dangerouslySetInnerHTML={{
          __html: title
        }}
      />
      <p
        className="text-base font-normal text-grey-200 mb-10"
        dangerouslySetInnerHTML={{
          __html: description
        }}
      />

      <div className="flex w-full justify-end">
        <EditVideoIcon />
      </div>
    </Link>
  );
};
